import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import {
  listCarrierNoteExistenceOptions,
  toMinutes,
  useApi,
  useHeyApi,
  type CarrierNoteExistenceResponse,
} from "@platform/api";
import { logger } from "@platform/browser-logger";

import type { CarrierNotePayload } from "../types";

const carrierNoteQueryKeys = {
  carrierNote: (
    rfpId: string,
    carrierId: string,
  ): ["carrier-notes", string, string] =>
    ["carrier-notes", rfpId, carrierId] as const,
  carrierNoteExistence: (rfpId: string): ["carrier-notes", string] =>
    ["carrier-notes", rfpId] as const,
};

const getCarrierNote = async (
  rfpId: string,
  carrierId: string,
  axiosInstance: { get: <T>(url: string) => Promise<{ data: T }> },
): Promise<CarrierNotePayload> => {
  try {
    const response = await axiosInstance.get<CarrierNotePayload>(
      `/btx/v1/rfps/${encodeURIComponent(rfpId)}/carriers/${encodeURIComponent(carrierId)}/note`,
    );
    return response.data;
  } catch (error: unknown) {
    if (
      typeof error === "object" &&
      error !== null &&
      "response" in error &&
      (error as { response?: { status?: number } }).response?.status === 404
    ) {
      return { content: "" };
    }
    throw error;
  }
};

const upsertCarrierNote = async (
  rfpId: string,
  carrierId: string,
  request: CarrierNotePayload,
  axiosInstance: {
    put: <T>(url: string, data: unknown) => Promise<{ data: T }>;
  },
): Promise<CarrierNotePayload> => {
  const response = await axiosInstance.put<CarrierNotePayload>(
    `/btx/v1/rfps/${encodeURIComponent(rfpId)}/carriers/${encodeURIComponent(carrierId)}/note`,
    request,
  );
  return response.data;
};

interface UseCarrierNoteExistenceOptions {
  ecoRfpId: string;
}

export const useCarrierNoteExistence = ({
  ecoRfpId,
}: UseCarrierNoteExistenceOptions): UseQueryResult<
  CarrierNoteExistenceResponse,
  Error
> => {
  const { btxClient } = useHeyApi();

  return useQuery({
    ...listCarrierNoteExistenceOptions({
      path: { id: ecoRfpId },
      client: btxClient,
    }),
    enabled: !!ecoRfpId,
  });
};

interface UseCarrierNoteOptions {
  ecoRfpId: string;
  carrierId: string;
}

export const useCarrierNote = ({
  ecoRfpId,
  carrierId,
}: UseCarrierNoteOptions): UseQueryResult<CarrierNotePayload, Error> => {
  const axiosInstance = useApi();

  return useQuery({
    queryKey: carrierNoteQueryKeys.carrierNote(ecoRfpId, carrierId),
    queryFn: () => getCarrierNote(ecoRfpId, carrierId, axiosInstance),
    enabled: !!ecoRfpId && !!carrierId,
    staleTime: toMinutes(5),
  });
};

interface CarrierNoteSaveVariables {
  carrierId: string;
  content: string;
}

interface UseUpsertCarrierNoteOptions {
  ecoRfpId: string;
  onSuccess?: (carrierId: string) => void;
  onError?: (carrierId: string) => void;
}

export const useUpsertCarrierNote = ({
  ecoRfpId,
  onSuccess,
  onError,
}: UseUpsertCarrierNoteOptions): UseMutationResult<
  CarrierNotePayload,
  Error,
  CarrierNoteSaveVariables
> => {
  const axiosInstance = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ carrierId, content }: CarrierNoteSaveVariables) =>
      upsertCarrierNote(ecoRfpId, carrierId, { content }, axiosInstance),
    onSuccess: (_data, { carrierId, content }) => {
      queryClient.setQueryData<CarrierNotePayload>(
        carrierNoteQueryKeys.carrierNote(ecoRfpId, carrierId),
        { content },
      );
      logger.debug("[useUpsertCarrierNote] Saved carrier note", {
        ecoRfpId,
        carrierId,
      });
      onSuccess?.(carrierId);
    },
    onError: (error, { carrierId }) => {
      logger.error("Failed to upsert carrier note", error, {
        ecoRfpId,
        carrierId,
      });
      onError?.(carrierId);
    },
  });
};
